import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input, ViewChild,
  ElementRef, Output, EventEmitter, OnChanges, SimpleChanges
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { isNullOrUndefined } from '@app/util/util';

import { OptionSelected } from '@booking/entities/booking-arrangement';
import { PERSON_FORM_INPUTS, PERSON_ADDRESS_FORM_INPUTS } from '@booking/constants/forms.constants';
import { FormInputsService } from '@reservation/services/form-inputs.service';
import { ProvincesByCity } from '@app/entities/provinces-cities';
import { FORM_COMPONENTS_PROPS } from '@forms/form-components/constants/form-components-props.constants';
import { BillingCity } from '@billing/entities/billing.payload';
import { Countries } from '@ux/entities/country.enum';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyColor, TypographyFontSize } from '@design-system/components/atoms/typography/entities/typography.types';

@Component({
  selector: 'app-form-state-selector',
  templateUrl: './form-state-selector.component.html',
  styleUrls: ['./form-state-selector.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FormStateSelectorComponent implements OnChanges {

  @Input() form: UntypedFormGroup;
  @Input() isSubmitted: boolean;
  @Input() isBilling: boolean = true;
  @Input() selector: string = PERSON_ADDRESS_FORM_INPUTS.STATE_CODE;
  @Input() defaultProvince: string;
  @Input() provinces: ProvincesByCity[] = [];
  @Input() cities: BillingCity[] = [];
  @Input() defaultCity: string;
  @Input() isMobile: boolean;

  @Output() selected: EventEmitter<string> = new EventEmitter();

  @ViewChild('state') public State: ElementRef;

  public readonly iconLocation = Icon.Location;

  public readonly fontSize = TypographyFontSize;
  public readonly fontColor = TypographyColor;

  constructor(
    private readonly formInputsService: FormInputsService
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.[FORM_COMPONENTS_PROPS.PROVINCES]) {
      if (this.defaultProvince && this.defaultIndex < 0) {
        this.defaultProvince = '';
        this.onSelected({ value: `${this.defaultProvince}${this.stateCodeForDisplay}`, index: 0 });
      }
    }
  }

  get addressForm(): UntypedFormGroup {
    return this.form?.get(PERSON_FORM_INPUTS.ADDRESS) as UntypedFormGroup;
  }

  get defaultIndex(): number {
    return this.provinces.findIndex(c => c.ProvinceCode === this.defaultProvince);
  }

  get defaultProvinceName(): string {
    return this.defaultIndex >= 0 ? 
      `${this.provinces[this.defaultIndex].ProvinceName}${this.stateCodeForDisplay}` : 
      '';
  }

  get options(): string[] {
    return this.provinces.map(city => 
      `${city.ProvinceName}${this.stateCodeForDisplay}`
    );
  }

  get defaultCityIndex(): number {
    return this.cities.findIndex(c => c.code === this.defaultCity);
  }

  get defaultStateCode(): string {
    return this.defaultCityIndex > -1 ? this.cities[this.defaultCityIndex].state_code.split('_')[1] : '';
  }

  get defaultCountryCode(): string {
    return this.defaultCityIndex > -1 ? this.cities[this.defaultCityIndex].countrycode : '';
  }

  get stateCodeForDisplay(): string {
    return this.defaultCountryCode === Countries.Brazil ? ` (${this.defaultStateCode})`: '';
  }

  public onSelected(state: OptionSelected): void {
    const { value } = state;
    const fullAdress = this.provinces
      .find(province => province.ProvinceName === value.toString()
        .replace(this.stateCodeForDisplay, ''));
    if (!isNullOrUndefined(fullAdress)) {
      this.addressForm.patchValue({ [PERSON_ADDRESS_FORM_INPUTS.STATE_CODE]: fullAdress.ProvinceCode });
      this.selected.emit(fullAdress.ProvinceCode);
    } else {
      this.addressForm.patchValue({ [PERSON_ADDRESS_FORM_INPUTS.STATE_CODE]: value.toString() });
      this.selected.emit(value.toString());
    }
    this.form?.patchValue({ [PERSON_FORM_INPUTS.ADDRESS]: this.addressForm.value });
  }

  public isFieldInvalid(field: string): boolean {
    return this.formInputsService.isFieldInvalid(field, this.addressForm, this.isSubmitted);
  }

}
